export class ApiError extends Error {
  status: number;
  data: any;

  constructor(message: string, status = 500, data: any = null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

export type AdminSession = {
  status?: string;
  authenticated: boolean;
  actor?: string | null;
  role?: 'admin' | 'viewer' | null;
  authKind?: 'password' | 'telegram' | null;
  telegramUserId?: string | null;
  phone?: string | null;
  csrfToken?: string | null;
  createdAt?: string | null;
  expiresAt?: string | null;
  message?: string;
};

function toQuery(params: Record<string, string | number | boolean | undefined>) {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    search.set(key, String(value));
  });
  const serialized = search.toString();
  return serialized ? `?${serialized}` : '';
}

export function createApiClient(getCsrfToken: () => string | null) {
  async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
    const headers = new Headers(options.headers || {});
    const method = (options.method || 'GET').toUpperCase();
    const unsafe = method !== 'GET' && method !== 'HEAD';

    if (unsafe) {
      const csrfToken = getCsrfToken();
      if (csrfToken) {
        headers.set('X-CSRF-Token', csrfToken);
      }
    }

    let body = options.body;
    if (body && typeof body === 'object' && !(body instanceof FormData) && !headers.has('Content-Type')) {
      headers.set('Content-Type', 'application/json');
      body = JSON.stringify(body);
    }

    const response = await fetch(path, {
      ...options,
      method,
      headers,
      body,
      credentials: 'include',
    });

    const text = await response.text();
    const data = text ? JSON.parse(text) : null;

    if (!response.ok) {
      throw new ApiError(data?.message || data?.error || `Request failed: ${response.status}`, response.status, data);
    }

    return data as T;
  }

  return {
    getSession: () => request<AdminSession>('/admin/api/session'),
    login: (password: string) => request<AdminSession>('/admin/api/session/login', {
      method: 'POST',
      body: { password } as any,
    }),
    telegramWebappAuth: (initData: string) => request<AdminSession>('/telegram/auth/webapp', {
      method: 'POST',
      body: { initData } as any,
    }),
    logout: () => request('/admin/api/session/logout', { method: 'POST' }),
    getOverview: () => request('/admin/api/overview'),
    getSyncStatus: (includeDetails = false) => request(`/admin/api/sync/status${includeDetails ? '?includeDetails=1' : ''}`),
    getSyncLog: (limit = 20, includeDetails = false) => request(`/admin/api/sync/log${toQuery({ limit, includeDetails: includeDetails ? 1 : undefined })}`),
    getCatalogHealth: () => request('/admin/api/catalog/health'),
    getOrdersPayments: (limit = 50) => request(`/admin/api/orders-payments${toQuery({ limit })}`),
    getNovaPoshtaOrders: (limit = 50, status?: string) => request(`/admin/api/nova-poshta/orders${toQuery({ limit, status })}`),
    archiveNovaPoshtaOrderHistory: () => request('/admin/api/nova-poshta/orders/archive-history', { method: 'POST' }),
    retryNovaPoshtaOrder: (id: number) => request(`/admin/api/nova-poshta/orders/${id}/retry`, { method: 'POST' }),
    createNovaPoshtaShipment: (jobId: number, payload: any) => request(`/admin/api/nova-poshta/orders/${jobId}/shipments`, {
      method: 'POST',
      body: payload,
    }),
    updateNovaPoshtaShipment: (id: number, payload: any) => request(`/admin/api/nova-poshta/shipments/${id}`, {
      method: 'PATCH',
      body: payload,
    }),
    createNovaPoshtaShipmentTtn: (id: number) => request(`/admin/api/nova-poshta/shipments/${id}/create-ttn`, { method: 'POST' }),
    syncNovaPoshtaShipmentsToShopify: (orderId: string | number) => request(`/admin/api/nova-poshta/orders/${orderId}/sync-shopify`, { method: 'POST' }),
    getSettings: () => request('/admin/api/settings'),
    getNovaPoshtaSenderDirectory: (senderRef?: string) => request(`/admin/api/nova-poshta/sender-directory${toQuery({ senderRef })}`),
    getTelegramSettings: () => request('/admin/api/telegram/settings'),
    updateTelegramRuntimeSettings: (payload: any) => request('/admin/api/telegram/runtime-settings', {
      method: 'POST',
      body: payload,
    }),
    createTelegramAccessRule: (payload: any) => request('/admin/api/telegram/access-rules', {
      method: 'POST',
      body: payload,
    }),
    updateTelegramAccessRule: (id: number, payload: any) => request(`/admin/api/telegram/access-rules/${id}`, {
      method: 'PATCH',
      body: payload,
    }),
    deleteTelegramAccessRule: (id: number) => request(`/admin/api/telegram/access-rules/${id}`, {
      method: 'DELETE',
    }),
    revealSettingSecret: (target: string, key: string) => request('/admin/api/settings/secret/reveal', {
      method: 'POST',
      body: { target, key } as any,
    }),
    previewSettings: (target: string, changes: Record<string, unknown>) => request('/admin/api/settings/preview', {
      method: 'POST',
      body: { target, changes } as any,
    }),
    applySettings: (target: string, changes: Record<string, unknown>, expectedRevision: string) => request('/admin/api/settings/apply', {
      method: 'POST',
      body: { target, changes, expectedRevision } as any,
    }),
    previewMaterializeDefaults: (target: string) => request('/admin/api/settings/defaults/preview', {
      method: 'POST',
      body: { target } as any,
    }),
    applyMaterializeDefaults: (target: string, expectedRevision: string) => request('/admin/api/settings/defaults/apply', {
      method: 'POST',
      body: { target, expectedRevision } as any,
    }),
    previewResetDefaults: (target: string, keys: string[]) => request('/admin/api/settings/reset/preview', {
      method: 'POST',
      body: { target, keys } as any,
    }),
    applyResetDefaults: (target: string, keys: string[], expectedRevision: string) => request('/admin/api/settings/reset/apply', {
      method: 'POST',
      body: { target, keys, expectedRevision } as any,
    }),
    runFullSync: () => request('/admin/api/actions/sync-full', { method: 'POST' }),
    runQueuedExport: () => request('/admin/api/actions/export-queued-orders', { method: 'POST' }),
    getAuditLog: (limit = 50) => request(`/admin/api/audit-log${toQuery({ limit })}`),
  };
}
